import React, { useEffect, useState } from "react";
import { Scatter } from "react-chartjs-2";
import { Chart as ChartJS, LinearScale, PointElement, Tooltip, Legend } from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

// Register Chart.js components
ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

// Styled components
const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 75vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 20px;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover {
    color: #000;
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
`;

const Select = styled.select`
  padding: 5px;
  font-size: 1rem;
  border: 1px solid #ccc;
  border-radius: 5px;
`;

const OutlierTransactions = ({ salesData }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [chartData, setChartData] = useState(null);

  const monthNames = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
  ];

  const calculateTransactionValue = (row) =>
    parseFloat(row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22) || 0;

  useEffect(() => {
    if (!salesData || salesData.length === 0) return;
    const years = [...new Set(salesData.map((sale) => parseInt(sale.Rok, 10)))].sort((a, b) => b - a);
    setAvailableYears(years);
    if (years.length > 0) setSelectedYear(years[0]);
  }, [salesData]);

  useEffect(() => {
    if (selectedYear) {
      const months = [...new Set(
        salesData
          .filter((sale) => parseInt(sale.Rok, 10) === selectedYear)
          .map((sale) => parseInt(sale.Miesiac, 10))
      )].sort((a, b) => a - b);
      setAvailableMonths(months);
    }
  }, [selectedYear, salesData]);

  useEffect(() => {
    if (!selectedYear) return;
    const filtered = salesData.filter((sale) =>
      parseInt(sale.Rok, 10) === selectedYear &&
      (selectedMonth === "all" || parseInt(sale.Miesiac, 10) === selectedMonth)
    );
    setFilteredData(filtered);
  }, [selectedYear, selectedMonth, salesData]);

  useEffect(() => {
    if (!filteredData || filteredData.length === 0) return;

const transactions = filteredData
  .map((sale, index) => ({
    id: index + 1,
    value: calculateTransactionValue(sale),
    customer: sale.NazwaKontrahenta || "Brak nazwy",
    document: sale.DowodSprzedazy || "Brak numeru",
  }))
  .filter((t) => t.value > 0)
  .sort((a, b) => a.value - b.value);


    if (transactions.length < 4) return;

    // Quartiles
    const values = transactions.map((t) => t.value);
    const Q1 = values[Math.floor(values.length * 0.25)];
    const Q3 = values[Math.floor(values.length * 0.75)];
    const IQR = Q3 - Q1;
    const lowerBound = Q1 - 2 * IQR;
    const upperBound = Q3 + 2 * IQR;

    const outliers = transactions.filter((t) => t.value < lowerBound || t.value > upperBound);
    const normalTransactions = transactions.filter((t) => t.value >= lowerBound && t.value <= upperBound);

setChartData({
  datasets: [
    {
      label: "Normalne transakcje",
      data: normalTransactions.map((t) => ({
        x: t.id,
        y: t.value,
        customer: t.customer,
        document: t.document,
      })),
      backgroundColor: "green",
      pointRadius: 4,
    },
    {
      label: "Transakcje poza normą (Outliers)",
      data: outliers.map((t) => ({
        x: t.id,
        y: t.value,
        customer: t.customer,
        document: t.document,
      })),
      backgroundColor: "red",
      pointRadius: 6,
    },
  ],
});

  }, [filteredData]);

  return (
    <ChartWrapper>
      <TitleWrapper>
        Transakcje poza normą
        <InfoIconWrapper data-tooltip-id="outlier-tooltip">
          <Info />
        </InfoIconWrapper>
      </TitleWrapper>

      <InfoTooltip
        id="outlier-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Transakcje poza normą</strong><br />
            Wykres scatter pokazuje wartości transakcji,<br />
            gdzie czerwone punkty to transakcje odstające<br />
            wykryte za pomocą IQR (rozstęp międzykwartylowy).
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label>Rok:</label>
          <Select value={selectedYear || ""} onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}>
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </div>

        <div>
          <label>Miesiąc:</label>
          <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value === "all" ? "all" : parseInt(e.target.value, 10))}>
            <option value="all">Cały rok</option>
            {availableMonths.map((month) => (
              <option key={month} value={month}>{monthNames[month - 1]}</option>
            ))}
          </Select>
        </div>
      </FiltersWrapper>

      {chartData ? (
        <Scatter
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { title: { display: true, text: "Transakcje" } },
              y: { title: { display: true, text: "Kwota (PLN)" } },
            },
            plugins: {
              legend: { position: "top" },
              title: {
            
              },
tooltip: {
  callbacks: {
    label: (context) => {
      const value = context.parsed.y;
      const raw = context.raw || {};
      const lines = [
        `Wartość: ${value.toLocaleString("pl-PL")} zł`,
      ];

      if (raw.customer) {
        lines.push(`Kontrahent: ${raw.customer}`);
      }
      if (raw.document) {
        lines.push(`Dokument: ${raw.document}`);
      }

      return lines; // każda linia = osobny wiersz w tooltipie
    },
  },
},

            },
          }}
        />
      ) : (
        <p>Brak transakcji dla wybranego okresu.</p>
      )}
    </ChartWrapper>
  );
};

export default OutlierTransactions;
